import ImageIcon from "@mui/icons-material/Image";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Snackbar,
  TextField,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Formik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import Header from "../../components/Header";

const Form = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const [previewImage, setPreviewImage] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const navigate = useNavigate();

  const handleImageChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    setFieldValue("blog-image", file);

    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewImage(null);
    }
  };

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("blog-image", values["blog-image"]);
      formData.append("author", values.author);
      formData.append("email", values.email);
      formData.append("noOfReaders", values.noOfReaders);
      formData.append("type", values.type);
      formData.append("status", values.status);
      formData.append("videoURL", values.videoURL); // Add videoURL to form data

      const token = localStorage.getItem("token");

      const response = await fetch(
        "https://backend.placemyfilms.com/blog/createBlog",
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        console.log("Blog created successfully:", responseData);
        setSnackbarMessage("Blog created successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);

        setPreviewImage(null);
        setTimeout(() => {
          navigate("/blog");
        }, 2000);
      } else {
        const errorData = await response.json();
        console.error("Error creating blog:", errorData);
        setSnackbarMessage("Error creating blog: " + errorData.message);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error creating blog:", error);
      setSnackbarMessage("Error creating blog: " + error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box m="20px" height="80vh" overflow="auto" paddingRight="20px">
      <Header title="CREATE NEW BLOG" subtitle="Create a new blog post" />

      <Formik
        initialValues={{
          title: "",
          description: "",
          author: "",
          email: "",
          noOfReaders: "0",
          type: "poem",
          status: "Active",
          "blog-image": null,
          videoURL: "", // Add videoURL initial value
        }}
        validationSchema={checkoutSchema}
        onSubmit={handleFormSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                label="Blog Title"
                name="title"
                value={values.title}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: "span 4" }}
              />
              <TextField
                fullWidth
                multiline
                variant="filled"
                rows={6}
                label="Description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />
              <TextField
                fullWidth
                variant="filled"
                label="Author"
                name="author"
                value={values.author}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.author && !!errors.author}
                helperText={touched.author && errors.author}
                sx={{ gridColumn: "span 4" }}
              />
              <TextField
                fullWidth
                variant="filled"
                label="Email"
                name="email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 4" }}
              />
              <TextField
                fullWidth
                variant="filled"
                label="Video URL"
                name="videoURL"
                value={values.videoURL}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.videoURL && !!errors.videoURL}
                helperText={touched.videoURL && errors.videoURL}
                sx={{ gridColumn: "span 4" }}
              />
              {/* <TextField
                fullWidth
                variant="filled"
                label="Number of Readers"
                type="number"
                name="noOfReaders"
                value={values.noOfReaders}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.noOfReaders && !!errors.noOfReaders}
                helperText={touched.noOfReaders && errors.noOfReaders}
                sx={{ gridColumn: "span 2" }}
              /> */}
              <TextField
                fullWidth
                select
                variant="filled"
                label="Type"
                name="type"
                value={values.type}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.type && !!errors.type}
                helperText={touched.type && errors.type}
                sx={{ gridColumn: "span 2" }}
              >
                <MenuItem value="poem">Poem</MenuItem>
                <MenuItem value="article">Article</MenuItem>
                <MenuItem value="story">Story</MenuItem>
              </TextField>
              <TextField
                fullWidth
                select
                variant="filled"
                label="Status"
                name="status"
                value={values.status}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.status && !!errors.status}
                helperText={touched.status && errors.status}
                sx={{ gridColumn: "span 4" }}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
              <Box sx={{ gridColumn: "span 4" }}>
                <label htmlFor="image-upload">
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      handleImageChange(event, setFieldValue)
                    }
                    style={{ display: "none" }}
                  />
                  <Button
                    variant="contained"
                    component="span"
                    color="secondary"
                    startIcon={<ImageIcon />}
                  >
                    Select Blog Image
                  </Button>
                </label>

                {previewImage && (
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{
                      width: 200,
                      height: 200,
                      gridColumn: "span 4",
                      marginTop: "10px",
                    }}
                  />
                )}

                {touched["blog-image"] && errors["blog-image"] && (
                  <Box
                    sx={{
                      color: "red",
                      fontSize: "0.875rem",
                      marginTop: "8px",
                    }}
                  >
                    {errors["blog-image"]}
                  </Box>
                )}
              </Box>
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                size="large"
                disabled={isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                <strong>Create New Blog</strong>
              </Button>
            </Box>
          </form>
        )}
      </Formik>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const checkoutSchema = yup.object().shape({
  title: yup.string().required("Title is required"),
  description: yup.string().required("Description is required"),
  author: yup.string().required("Author is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  noOfReaders: yup.number().required("Number of readers is required"),
  type: yup.string().required("Type is required"),
  status: yup.string().required("Status is required"),
  "blog-image": yup.mixed().nullable().required("Blog image is required"),
  videoURL: yup.string().required("Video URL is required"), // Add videoURL validation
});

export default Form;